import React, { useContext, useEffect, useState } from "react";
import {
  AlertContext,
  UserContext,
  useStepperContext,
} from "../../context/MainContext";
import { propertyCreate } from "../../actions/propertyCreate";
import { useNavigate } from "react-router-dom";
import UploadWidget from "../UploadWidget";
import Modal from "../Modal";
import {
  TikTokEmbed,
  InstagramEmbed,
  FacebookEmbed,
  YouTubeEmbed,
  XEmbed,
  PinterestEmbed,
  LinkedInEmbed,
} from "react-social-media-embed";

const Complete = ({ uploadedItems, setUploadedItems, modal, setModal }) => {
  const { propertyData, setPropertyData } = useStepperContext();

  const { user, setUser } = useContext(UserContext);
  const { alert, setAlert } = useContext(AlertContext);
  const [option, setOption] = useState(null);
  const [waitingMessage, setWaitingMessage] = useState(null);

  // Safely compute features - ensure propertyData is an object
  const features =
    propertyData && typeof propertyData === "object"
      ? Object.keys(propertyData).filter(
          (val) =>
            propertyData[val] === "Yes" &&
            val !== "pets_allowed" &&
            val !== "security_deposit"
        )
      : [];
  const nav = useNavigate();

  useEffect(() => {
    // Success flow: propertyData set to false by action
    if (propertyData === false) {
      setAlert([
        "Property Creation Success!",
        "Congratulations your property has been uploaded, search for your given property ID to view.",
        "confirm",
      ]);
      nav("/");
    }
    // Failure flow: action sets propertyData to an error object
    else if (
      propertyData &&
      typeof propertyData === "object" &&
      propertyData.__error
    ) {
      setAlert(["Property Creation Failed", propertyData.__error, "failed"]);
      // Ensure confirmation modal is closed if still open
      if (modal) setModal(false);
    }
  }, [propertyData]);

  // Update alert when waiting message changes (for cold start feedback)
  useEffect(() => {
    if (waitingMessage) {
      setAlert([
        "Please Wait",
        `${waitingMessage}\n\nDo not close this window or navigate away.`,
        "waiting",
      ]);
    }
  }, [waitingMessage]);

  // Validate required fields before submission
  const validateRequiredFields = () => {
    const requiredFields = {
      county: "County",
      city: "City",
      street_name: "Street Name",
      for: "For (Sale/Lease)",
      type: "Property Type",
    };

    const missing = [];
    for (const [field, label] of Object.entries(requiredFields)) {
      const value = propertyData?.[field];
      if (!value || (typeof value === "string" && !value.trim())) {
        missing.push(label);
      }
    }

    return missing;
  };

  useEffect(() => {
    if (option) {
      // Validate required fields before submission
      const missingFields = validateRequiredFields();
      if (missingFields.length > 0) {
        setAlert([
          "Missing Required Fields",
          `Please fill in the following required fields before submitting: ${missingFields.join(
            ", "
          )}`,
          "failed",
        ]);
        setOption(null);
        return;
      }

      // Validate at least one picture is uploaded
      if (!uploadedItems || uploadedItems.length === 0) {
        setAlert([
          "Photo Required",
          "Please upload at least one photo of the property before submitting.",
          "failed",
        ]);
        setOption(null);
        return;
      }

      if (!user?.user_id) {
        setAlert([
          "Session Expired",
          "Please log in again to create a property.",
          "failed",
        ]);
        setOption(null);
        return;
      }

      setModal(!modal);
      setAlert([
        "Your request for creating a property has been submitted.",
        "Once it has been processed you will receive an update.",
        "waiting",
      ]);
      propertyCreate(
        propertyData,
        setPropertyData,
        setUser,
        features,
        user?.user_id,
        uploadedItems,
        { setWaitingMessage }
      );
    } else if (option === false) {
      setPropertyData(null);
      setModal(!modal);
      nav("/");
    }
  }, [option]);

  const addCommas = (num) => {
    if (!num) return "";
    num = num.replace(/\D/g, "");
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Normalize URLs to ensure they have a proper scheme for video fields
    const videoFields = [
      "embeded_url",
      "embeded_url2",
      "embeded_url3",
      "embeded_url4",
    ];
    if (videoFields.includes(name) && value) {
      const trimmed = value.trim();
      if (trimmed && !/^https?:\/\//i.test(trimmed)) {
        processedValue = `https://${trimmed}`;
      } else {
        processedValue = trimmed;
      }
    }

    // Input restrictions and formatting
    if (
      [
        "zip",
        "street_number",
        "beds_total",
        "acreage",
        "living_sqft",
        "living_areas",
        "est_year_built",
      ].includes(name)
    ) {
      let digitsOnly = value.replace(/\D/g, "");
      let maxLen = 6;
      let useCommas = false;
      if (name === "zip") maxLen = 5;
      if (name === "street_number") maxLen = 6;
      if (name === "beds_total") maxLen = 2;
      if (name === "acreage") {
        maxLen = 6;
        useCommas = true;
      }
      if (name === "living_sqft") {
        maxLen = 6;
        useCommas = true;
      }
      if (name === "living_areas") maxLen = 2;
      if (name === "est_year_built") maxLen = 4;
      digitsOnly = digitsOnly.slice(0, maxLen);
      processedValue = useCommas ? addCommas(digitsOnly) : digitsOnly;
    }

    setPropertyData({ ...propertyData, [name]: processedValue });
  };

  const normalizeSocialUrl = (url) => {
    if (!url) return "";
    let trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }
    return trimmed;
  };

  const getVideoEmbedData = (url) => {
    const normalized = normalizeSocialUrl(url);
    if (!normalized) return { provider: null, embedUrl: "" };

    const lower = normalized.toLowerCase();

    if (lower.includes("tiktok.com")) {
      return {
        provider: "tiktok",
        embedUrl: normalized,
      };
    }

    const instaMatch = normalized.match(
      /instagram\.com\/(p|reel|tv)\/([^/?#]+)/i
    );
    if (instaMatch) {
      return {
        provider: "instagram",
        embedUrl: normalized,
      };
    }
    if (lower.includes("instagram.com")) {
      return { provider: "instagram", embedUrl: normalized };
    }

    if (lower.includes("facebook.com") || lower.includes("fb.watch")) {
      return {
        provider: "facebook",
        embedUrl: normalized,
      };
    }

    if (lower.includes("linkedin.com")) {
      return { provider: "linkedin", embedUrl: normalized };
    }

    if (lower.includes("pinterest.com")) {
      return { provider: "pinterest", embedUrl: normalized };
    }

    if (lower.includes("x.com") || lower.includes("twitter.com")) {
      return { provider: "x", embedUrl: normalized };
    }

    if (
      lower.includes("youtube.com/watch") ||
      lower.includes("youtu.be") ||
      lower.includes("youtube.com/shorts")
    ) {
      return { provider: "youtube", embedUrl: normalized };
    }

    return { provider: "default", embedUrl: normalized };
  };

  const renderVideoEmbed = (url) => {
    if (!url) return null;
    const { provider, embedUrl } = getVideoEmbedData(url);
    if (!embedUrl) return null;

    return (
      <div className="flex justify-center py-4">
        {provider === "tiktok" && <TikTokEmbed url={embedUrl} />}
        {provider === "instagram" && <InstagramEmbed url={embedUrl} />}
        {provider === "facebook" && <FacebookEmbed url={embedUrl} />}
        {provider === "youtube" && <YouTubeEmbed url={embedUrl} />}
        {provider === "linkedin" && <LinkedInEmbed url={embedUrl} />}
        {provider === "pinterest" && <PinterestEmbed url={embedUrl} />}
        {provider === "x" && <XEmbed url={embedUrl} />}
      </div>
    );
  };

  return (
    <div className="w-full">
      {modal && (
        <Modal modal={modal} setModal={setModal} setOption={setOption} />
      )}
      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Images card */}
        <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-6 flex-1">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">Upload Images</h2>
          </div>
          <div>
            <UploadWidget
              setUploadedItems={setUploadedItems}
              userId={user?.user_id}
            />
          </div>
        </div>

        {/* Featured Videos card - up to 4 videos */}
        <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-6 w-full md:w-[420px]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900">
              Featured Videos (Up to 4)
            </h2>
          </div>

          <p className="text-xs text-gray-600 mb-4">
            Paste any valid URL to a video. Accepts links from{" "}
            <strong>TikTok, Instagram, Facebook, YouTube, Vimeo</strong>, or any
            other platform.
          </p>

          <div className="flex flex-col gap-4">
            {/* Video 1 */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700 mb-1">
                Video 1 (Primary)
              </label>
              <input
                className="w-full bg-slate-100 border border-slate-200 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                name="embeded_url"
                type="text"
                placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                value={propertyData["embeded_url"] || ""}
                onChange={handleChange}
              />
            </div>

            {/* Video 2 */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700 mb-1">
                Video 2 (Optional)
              </label>
              <input
                className="w-full bg-slate-100 border border-slate-200 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                name="embeded_url2"
                type="text"
                placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                value={propertyData["embeded_url2"] || ""}
                onChange={handleChange}
              />
            </div>

            {/* Video 3 */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700 mb-1">
                Video 3 (Optional)
              </label>
              <input
                className="w-full bg-slate-100 border border-slate-200 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                name="embeded_url3"
                type="text"
                placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                value={propertyData["embeded_url3"] || ""}
                onChange={handleChange}
              />
            </div>

            {/* Video 4 */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-slate-700 mb-1">
                Video 4 (Optional)
              </label>
              <input
                className="w-full bg-slate-100 border border-slate-200 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                name="embeded_url4"
                type="text"
                placeholder="https://www.youtube.com/... or https://www.tiktok.com/..."
                value={propertyData["embeded_url4"] || ""}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Complete;
